// src/pages/Dashboard.jsx
import { useEffect, useState, useCallback } from 'react';
import { api, getTokens } from '../lib/api.js';
import CaseCard from '../components/CaseCard.jsx';

export default function Dashboard({ mineOnly = false }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const qs = mineOnly ? '?mine=true' : '';
      const body = await api(`/cases${qs}`);
      setCases(body.cases);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [mineOnly]);

  useEffect(() => { load(); }, [load]);

  // Live updates: this staff member's personal SSE channel (Module 5) tells
  // us to re-fetch on case_assigned. A full re-fetch (not a manual patch)
  // keeps the queue provably consistent with the server after any event.
  //
  // Access tokens are short-lived (15 min) by design (Module 2). A plain
  // EventSource has no way to refresh mid-connection — once the token
  // baked into its URL expires, it just fails silently forever. So we
  // proactively tear down and reopen the connection every 10 minutes with
  // a freshly fetched token, comfortably inside the 15-minute window,
  // rather than waiting for it to fail.
  useEffect(() => {
    let es;
    let renewTimer;

    function connect() {
      const { accessToken } = getTokens();
      if (!accessToken) return;
      es = new EventSource(`/api/realtime/stream?access_token=${encodeURIComponent(accessToken)}`);
      es.onmessage = (evt) => {
        load();
        try {
          const data = JSON.parse(evt.data);
          if ((data.type === 'case_assigned' || data.type === 'escalation_alert') && Notification.permission === 'granted' && document.hidden) {
            new Notification('Sauti — new case', {
              body: `${data.caseRef ?? 'A case'} needs attention${data.priority ? ` (P${data.priority})` : ''}.`,
              tag: data.caseId,
            });
          }
        } catch { /* not all events carry parseable payloads */ }
      };
      es.onerror = () => es.close(); // renewTimer below will reconnect on schedule
    }

    connect();
    renewTimer = setInterval(() => {
      es?.close();
      connect();
    }, 10 * 60 * 1000);

    return () => {
      clearInterval(renewTimer);
      es?.close();
    };
  }, [load]);

  // Ask for browser-notification permission once, gently — not on first
  // paint (that's the classic "grant me things" dark pattern), only after
  // the person has actually landed on their queue.
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const t = setTimeout(() => Notification.requestPermission(), 2000);
      return () => clearTimeout(t);
    }
  }, []);

  const kpis = [
    { label: 'Open cases', value: cases.filter((c) => !['closed', 'archived'].includes(c.status)).length },
    { label: 'Priority 1', value: cases.filter((c) => c.priority === 1).length, tone: 'text-p1' },
    { label: 'Unassigned', value: cases.filter((c) => !c.assigned_staff_id && !['closed', 'archived'].includes(c.status)).length },
  ];

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-charcoal">{mineOnly ? 'My cases' : 'Case queue'}</h1>

      <div className="mt-4 grid grid-cols-3 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-ink/10 bg-white p-4">
            <div className={`text-2xl font-mono font-semibold ${k.tone ?? 'text-charcoal'}`}>{k.value}</div>
            <div className="text-xs text-ink/60 mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-2">
        {loading && <p className="text-sm text-ink/50">Loading queue…</p>}
        {error && <p className="text-sm text-p1" role="alert">{error}</p>}
        {!loading && cases.length === 0 && (
          <div className="rounded-lg border border-dashed border-ink/20 p-8 text-center text-sm text-ink/50">
            No cases here right now.
          </div>
        )}
        {cases.map((c) => <CaseCard key={c.id} c={c} />)}
      </div>
    </div>
  );
}
